import { ArrayUtil, DebouncedFunction, DOMUtil, HorizontalAlignment, StringUtil } from "@mcleod/core";
import { HorizontalSpacer } from "../../page/HorizontalSpacer";
import { ImageName } from "@mcleod/images";
import { Image } from "../image/Image";
import { Label } from "../label/Label";
import { LabelSizer } from "../label/LabelSizer";
import { Layout } from "../layout/Layout";
import { Panel } from "../panel/Panel";
import { ReadMoreType } from "../label/ReadMoreType";
import { Textbox } from "./Textbox";
import { SeparatorPanel } from "../../page/SeparatorPanel";
import { Button } from "../button/Button";
import { ButtonVariant } from "../button/ButtonVariant";
import { ListLayoutLauncher } from "../../page/ListLayoutLauncher";

/**
 * Generates the tooltip for a multi-select type-ahead field (or its label when in printable mode).  When a
 * quick info layout is specified, the tooltip will contain a list of the selected items' display values
 * on the left-hand side of the tooltip, and the quick info layout on the right-hand side of the tooltip.
 * Hovering over a value in the list will display that value's information in the quick info layout.
 *
 * When no quick info layout is specified, the tooltip will contain only the list of the selected itms.  The tooltip
 * will have a static/arbitrary max height; if the selected items do not fit in that height then the tooltip will scroll
 * to allow all of the items in the list to be viewed.
 *
 * Each item in the list will offer a delete button to allow for removal of that item from the related Textbox.
 *
 * @returns A Panel containing the content of the tooltip to be rendered.
 */
export class LookupModelMultiSelectTooltip {
    private displayedItem: Panel;
    private displayValues: string[];
    private items: Panel[];
    private panelList: Panel;
    private panelLower: Panel;
    private panelOuter: Panel;
    private quickInfoLayout: Layout;
    private resultValues: string[];
    private textbox: Textbox;
    private isBuilding: boolean = false;

    constructor(textbox: Textbox, displayValues: string[], resultValues: string[]) {
        this.textbox = textbox;
        this.displayValues = displayValues;
        this.resultValues = resultValues;
    }

    async build(): Promise<Panel> {
        this.isBuilding = true;
        const hasQuickInfoLayout = this.hasQuickInfoLayout();
        this.panelOuter = this.createOuterPanel();
        if (hasQuickInfoLayout) {
            this.panelOuter.add(this.createInstructionLabel());
        } else {
            const titlePanel = this.createTitlePanel();
            if (titlePanel != null) {
                this.panelOuter.add(titlePanel);
            }
        }
        this.panelLower = this.createLowerPanel();
        this.panelOuter.add(this.panelLower);
        this.panelList = this.createListPanel(hasQuickInfoLayout);
        this.panelLower.add(this.panelList);
        if (hasQuickInfoLayout) {
            this.panelLower.add(new SeparatorPanel(this.panelLower, true, 100));
            this.quickInfoLayout = await this.createQuickInfoLayout();
            const titlePanel = this.createTitlePanel();
            if (titlePanel != null) {
                this.panelOuter.insert(titlePanel, 0);
            }
            this.panelLower.add(this.quickInfoLayout);
            this.panelList.maxHeight = this.quickInfoLayout.height;
            this.displayFirstItem();
            this.addHoverListeners();
        }
        this.isBuilding = false;
        return this.panelOuter;
    }

    private hasQuickInfoLayout(): boolean {
        return StringUtil.isEmptyString(this.textbox.quickInfoLayout) !== true;
    }

    get(): Panel {
        return this.panelOuter;
    }

    private createOuterPanel(): Panel {
        return new Panel({
            id: "multiSelectLookupModelTooltip"
        });
    }

    private createTitleLabel(): Label {
        const quickInfoTitlePlural = this.quickInfoLayout?.titlePlural;
        const text = !StringUtil.isEmptyString(quickInfoTitlePlural) ?
            `${quickInfoTitlePlural} Selected` :
            "Selected Values";
        return new Label({ text , fontBold: true, fontSize: "large", rowBreak: false });
    }

    private createTitlePanel(): Panel {
        const panel = Panel.createNoMarginNoPaddingPanel({
            id: "multiSelectLookupModelTooltip-titlePanel",
            components: [this.createTitleLabel()]
        });

        if (this.textbox.listLayout) {
            panel.add(this.createDrillDownButton());
        }

        return panel;
    }

    private createDrillDownButton(): Button {
        const button = new Button({
            id: "multiSelectLookupModelTooltip-drillDownButton",
            color: "primary",
            imageName: ImageName.arrowGoTo,
            variant: ButtonVariant.round,
            onClick: () => button.makeBusyWhile(ListLayoutLauncher.open({
                listLayout: this.textbox.listLayout,
                keyValues: this.resultValues
            }))
        });
        return button;
    }

    private createLowerPanel(): Panel {
        return Panel.createNoMarginNoPaddingPanel({
            id: "multiSelectLookupModelTooltip-lower",
            fillRow: true
        });
    }

    private createInstructionLabel(): Label {
        return new Label({
            text: "Hover over an item in the list to see more information.",
            fontSize: "small",
            marginBottom: 14,
            height: 16,
            color: "component.palette.textbox.caption.color",
            allowSelect: false,
            readMoreType: ReadMoreType.NONE
        });
    }

    private createListPanel(hasQuickInfoLayout: boolean): Panel {
        const result = Panel.createNoMarginNoPaddingPanel({
            id: "multiSelectLookupModelTooltip-listPanel",
            paddingRight: hasQuickInfoLayout ? 12 : null,
            rowBreak: false,
            scrollY: true
        });
        this.items = this.createListItems(hasQuickInfoLayout);
        result.add(...this.items);
        return result;
    }

    private createListItems(hasQuickInfoLayout: boolean): Panel[] {
        const result: Panel[] = [];
        let longestTab = 0;
        for (let x=0 ; x<this.displayValues.length ; x++) {
            const listItem = this.createListItem(x, hasQuickInfoLayout);
            result.push(listItem);
            listItem.doWhileDimensionsComputed(() => {
                longestTab = Math.max(DOMUtil.getElementWidth(listItem._element, { includePadding: true }), longestTab);
            });
        }
        const widestAllowed = hasQuickInfoLayout ? 200 : 600;
        const sizeToUse = Math.min(longestTab, widestAllowed);
        result.forEach(listItem => listItem.width = sizeToUse);
        return result;
    }

    private createListItem(index: number, hasQuickInfoLayout: boolean): Panel {
        const displayValue = this.displayValues[index];
        const resultValue = this.resultValues[index];
        const itemPanel = new Panel({
            backgroundColor: "subtle.lightest",
            borderRadius: 2,
            id: `item-${resultValue}`,
            margin: 4,
            maxWidth: hasQuickInfoLayout ? 200 : 600,
            align: HorizontalAlignment.LEFT,
            rowBreakDefault: false,
            wrap: false
        });
        const label = new Label({
            text: displayValue,
            wrap: false
        });
        itemPanel.add(label);
        if (this.textbox.enabled && !this.textbox.printable) {
            const image = new Image({
                name: ImageName.circleCancel,
                height: 21,
                width: 21,
                onClick: () => this.removeListItem(itemPanel, resultValue)
            });
            itemPanel.add(new HorizontalSpacer({ padding: 0 }), image);
        }
        new LabelSizer(itemPanel, label).sizeForMaxWidth();
        return itemPanel;
    }

    private removeListItem(itemPanel: Panel, resultValue: any) {
        this.textbox._deleteSelectedLookupModelItem(resultValue);
        this.panelList.remove(itemPanel);
        const itemIndex = this.items.indexOf(itemPanel);
        this.items.splice(itemIndex, 1);
        this.displayValues.splice(itemIndex, 1);
        this.resultValues.splice(itemIndex, 1);
        this.displayedItem = null;
        if (ArrayUtil.isEmptyArray(this.items)) {
            this.textbox.tooltipInstance?.remove();
        } else {
            // Depending on where in the list the deleted item was, the user's mouse position may begin to hover over
            // another item in the list, which will cause it's info to be displayed.  If that doesn't happen, display
            // the info for the first item in the list.
            setTimeout(() => {
                if (this.displayedItem == null) {
                    this.displayFirstItem();
                }
            }, 450);
        }
    }

    private addHoverListeners() {
        this.items.forEach(item => {
            item.addMouseEnterListener(() => this.debouncedDisplayInfoForItem(item));
        });
    }

    private debouncedDisplayInfoForItem = DebouncedFunction.create((item: Panel) => {
        this.displayInfoForItem(item);
    }, 300);

    private displayInfoForItem(item: Panel) {
        if (item !== this.displayedItem) {
            if (this.quickInfoLayout != null) {
                // avoid searching if the tooltip was dismissed
                if (!this.isBuilding && !this.quickInfoLayout.isMounted) {
                    return;
                }
                const itemIndex = this.items.indexOf(item);
                const resultValue = this.resultValues[itemIndex];
                this.quickInfoLayout.mainDataSource?.search({ search: resultValue });
                this.syncItemColors(item);
            }
            this.displayedItem = item;
        }
    }

    private displayFirstItem() {
        if (!ArrayUtil.isEmptyArray(this.items)) {
            this.displayInfoForItem(ArrayUtil.getFirstElement(this.items));
        }
    }

    private syncItemColors(selectedItem: Panel) {
        this.items.forEach(item => {
            item.backgroundColor = (selectedItem === item) ? "primary.lightest" : "subtle.lightest";
        });
    }

    private async createQuickInfoLayout(): Promise<Layout> {
        const quickInfoLayout = await Layout.getLoadedLayout(this.textbox.quickInfoLayout);
        quickInfoLayout.marginLeft = 8;
        const prevFillHeight = quickInfoLayout.fillHeight;
        quickInfoLayout.fillHeight = false;
        quickInfoLayout.minHeight = 140;
        quickInfoLayout.doWhileDimensionsComputed(() => {
            const quickInfoHeightWidth = DOMUtil.getElementHeightWidth(quickInfoLayout._element);
            quickInfoLayout.height = quickInfoHeightWidth.height;
            quickInfoLayout.width = quickInfoHeightWidth.width;
        });
        quickInfoLayout.fillHeight = prevFillHeight;
        return quickInfoLayout;
    }
}
